import React from "react";
import { IDropdownOption, Spinner, SpinnerSize, Icon } from "@fluentui/react";
import { ExcelDocumentIcon, ExcelLogoIcon } from "@fluentui/react-icons";
import { ThemeContext } from '@fluentui/react-theme-provider';
import DocumentTitle from "react-document-title";
import { ReactComponent as IncrementIcon } from '../../images/increment.svg';
import { ReactComponent as DecrementIcon } from '../../images/decrement.svg';
import { ActionButton, Card, CardData, CardsContainer, CardTitle, Container, GraphicsContainer, GraphicTitle, HeaderContainer, Label, MetricCardData, MetricsContainer, StyledDropdown, CounterButton, DatePicker, EditButton, DeleteButton, MainButton, ExportIcon } from "./SummaryPortfolioModel/Styles/SummaryPortfolioModelStyles";
import Highstock, { GraphicType } from "../../components/Highstock/Highstock";
import PortfolioModelTable, { IPortfolioModelTable } from "./SummaryPortfolioModel/Components/PortfolioModelTable";
import portfolioModelWallets from "../../services/PortfolioModelWalletService";
import { useLocation, useHistory } from 'react-router-dom';
import { PortfolioModelType } from "../../components/SavePlans/types/SavePlansTypes";
import FundService, { IPortfolioModelFullInformation } from "../../services/FundService";
import RecordsModal from "./SummaryPortfolioModel/Components/RecordsModal";
import ImportComposition from "./SummaryPortfolioModel/Components/ImportComposition";

interface LocationState
{
    portfolioModelId?: number;
    portfolioModelName?: string;
    portfolioModels?: PortfolioModelType[];
    savingPlanName?: string;
    funds?: any[];
    savingPlanId?: number;
    contract?: string;
}
interface CachedPortfolioData
{
    tableData: IPortfolioModelTable[];
    chartData: any[];
    isNoChartDataOnly?: boolean;
}

interface ICompositionRecord
{
    id: string;
    fecha: string;
    composicionCartera: string;
    portfolioModelName?: string;
    tableData: IPortfolioModelTable[];
}

interface SerieData
{
    date: string;
    cartera: number;
}

const SummaryPortfolioModel = () =>
{
    const theme = React.useContext(ThemeContext);
    const location = useLocation<LocationState>();
    const history = useHistory();
    const [selectedOptions, setSelectedOptions] = React.useState<string[]>(location.state?.portfolioModelId ? [location.state.portfolioModelId.toString()] : []);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isInterfaceDisabled, setIsInterfaceDisabled] = React.useState(false);
    const [series, setSeries] = React.useState<any[]>([]);
    const [modelPortfolioComposition, setModelPortfolioComposition] = React.useState<IPortfolioModelTable[]>([]);
    const [loadingPortfolios, setLoadingPortfolios] = React.useState<Set<string>>(new Set());
    const [noDataAvailable, setNoDataAvailable] = React.useState<boolean>(false);
    const [noChartDataOnly, setNoChartDataOnly] = React.useState<boolean>(false);
    const [counterValue] = React.useState<number>(101);
    const [selectedDate, setSelectedDate] = React.useState<string>(new Date().toISOString().split('T')[0]);
    const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);
    const [isImportModalOpen, setIsImportModalOpen] = React.useState<boolean>(false);
    const [selectedCompositionRecord, setSelectedCompositionRecord] = React.useState<string | null>(null);
    
    // Datos de ejemplo para composiciones - posteriormente se alimentarán con datos reales
    const [modalRecords] = React.useState([
    {
        id: "1",
        fecha: "15/01/2024",
        composicionCartera: "Renta Variable 60% - Renta Fija 40%",
        portfolioModelName: "Cartera Conservadora"
    },
    {
        id: "2",
        fecha: "20/01/2024",
        composicionCartera: "Renta Variable 65% - Renta Fija 35%",
        portfolioModelName: "Cartera Conservadora"
    },
    {
        id: "3",
        fecha: "25/01/2024",
        composicionCartera: "Renta Variable 70% - Renta Fija 30%",
        portfolioModelName: "Cartera Agresiva"
    },
    {
        id: "4",
        fecha: "30/01/2024",
        composicionCartera: "Renta Variable 55% - Renta Fija 45%",
        portfolioModelName: "Cartera Agresiva"
    },
    {
        id: "5",
        fecha: "05/02/2024",
        composicionCartera: "Renta Variable 62% - Renta Fija 38%",
        portfolioModelName: "Cartera Moderada"
    }]);

    // Datos de ejemplo para composiciones detalladas
    const exampleCompositions: ICompositionRecord[] = React.useMemo(() => [
    {
        id: "1",
        fecha: "15/01/2024",
        composicionCartera: "Renta Variable 60% - Renta Fija 40%",
        portfolioModelName: "Cartera Conservadora",
        tableData: [
            {
                nameFund: "Fondo Renta Variable Europa",
                isin: "ES0123456789",
                portfolioPercentage: 30.0,
                ytdLinearReturn: 8.5,
                oneYearVolatility: 15.2,
                fundPrice: 125.50,
                liquidationDate: "15/01/2024",
                currency: "EUR",
                ironiaPoints: 85,
                portfolioName: "Cartera Conservadora"
            },
            {
                nameFund: "Fondo Renta Fija Global",
                isin: "ES0987654321",
                portfolioPercentage: 40.0,
                ytdLinearReturn: 3.2,
                oneYearVolatility: 5.8,
                fundPrice: 98.75,
                liquidationDate: "15/01/2024",
                currency: "EUR",
                ironiaPoints: 78,
                portfolioName: "Cartera Conservadora"
            },
            {
                nameFund: "Fondo Mixto Equilibrado",
                isin: "ES0456789123",
                portfolioPercentage: 30.0,
                ytdLinearReturn: 5.8,
                oneYearVolatility: 10.5,
                fundPrice: 110.25,
                liquidationDate: "15/01/2024",
                currency: "EUR",
                ironiaPoints: 82,
                portfolioName: "Cartera Conservadora"
            }
        ]
    },
    {
        id: "2",
        fecha: "20/01/2024",
        composicionCartera: "Renta Variable 65% - Renta Fija 35%",
        portfolioModelName: "Cartera Conservadora",
        tableData: [
            {
                nameFund: "Fondo Renta Variable Europa",
                isin: "ES0123456789",
                portfolioPercentage: 35.0,
                ytdLinearReturn: 9.2,
                oneYearVolatility: 15.8,
                fundPrice: 127.30,
                liquidationDate: "20/01/2024",
                currency: "EUR",
                ironiaPoints: 87,
                portfolioName: "Cartera Conservadora"
            },
            {
                nameFund: "Fondo Renta Fija Global",
                isin: "ES0987654321",
                portfolioPercentage: 35.0,
                ytdLinearReturn: 2.8,
                oneYearVolatility: 6.2,
                fundPrice: 99.10,
                liquidationDate: "20/01/2024",
                currency: "EUR",
                ironiaPoints: 76,
                portfolioName: "Cartera Conservadora"
            },
            {
                nameFund: "Fondo Mixto Equilibrado",
                isin: "ES0456789123",
                portfolioPercentage: 30.0,
                ytdLinearReturn: 6.5,
                oneYearVolatility: 11.2,
                fundPrice: 112.50,
                liquidationDate: "20/01/2024",
                currency: "EUR",
                ironiaPoints: 84,
                portfolioName: "Cartera Conservadora"
            }
        ]
    }
    ], []);
    const options: IDropdownOption[] = React.useMemo(() => location.state?.portfolioModels ? location.state.portfolioModels.map(model =>
    ({
        key: model.id.toString(),
        text: model.name
    })) : []
    , [location.state?.portfolioModels]);

    const calculateMetrics = React.useCallback((chartData: any[]) =>
    {
        if(!chartData || chartData.length === 0)
        {
            return{
                fromStart: "0,00",
                lastSixMonths: "0,00",
                ytd: "0,00",
                year: "0,00"
            };
        }

        const sortedData = [...chartData].sort((a, b) => a[0] - b[0]);
        const firstValue = sortedData[0][1];
        const lastValue = sortedData[sortedData.length - 1][1];

        const fromStart = ((lastValue - firstValue) / Math.abs(firstValue)) * 100;
        const now = new Date();
        const currentYear = now.getFullYear();
        const startOfYear = new Date(currentYear, 0, 1).getTime();
        const oneYearAgo = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate()).getTime();
        
        // YTD (desde inicio del año actual)
        const ytdStartData = sortedData.find(item => item[0] >= startOfYear);
        const ytdStart = ytdStartData ? ytdStartData[1] : firstValue;
        const ytd = ((lastValue - ytdStart) / Math.abs(ytdStart)) * 100;

        // Año (últimos 12 meses)
        const yearStartData = sortedData.find(item => item[0] >= oneYearAgo);
        const yearStart = yearStartData ? yearStartData[1] : firstValue;
        const year = ((lastValue - yearStart) / Math.abs(yearStart)) * 100;

        // Últimos 6 meses
        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()).getTime();
        const sixMonthsStartData = sortedData.find(item => item[0] >= sixMonthsAgo);
        const sixMonthsStart = sixMonthsStartData ? sixMonthsStartData[1] : firstValue;
        const lastSixMonths = ((lastValue - sixMonthsStart) / Math.abs(sixMonthsStart)) * 100;

        const formatPercentage = (value: number) => value.toFixed(2).replace('.', ',');

        return{
            fromStart: formatPercentage(fromStart),
            lastSixMonths: formatPercentage(lastSixMonths),
            ytd: formatPercentage(ytd),
            year: formatPercentage(year)
        };
    }, []);

    // Métricas calculadas dinámicamente
    const metrics = React.useMemo(() =>
    {
        // Solo calculamos métricas si hay exactamente una cartera seleccionada
        if(selectedOptions.length === 1 && series.length === 1)
        {
            return calculateMetrics(series[0].data);
        }
        
        // Valores por defecto cuando hay múltiples carteras o ninguna
        return{
            fromStart: "0,00",
            lastSixMonths: "0,00",
            ytd: "0,00",
            year: "0,00"
        };
    }, [selectedOptions.length, series, calculateMetrics]);

    const getStorageKey = (portfolioModelId: string) => `portfolioModel_${portfolioModelId}`;
    const getChartStorageKey = (portfolioModelId: string) => `portfolioModelChart_${portfolioModelId}`;
    const saveToSessionStorage = React.useCallback((portfolioModelId: string, data: CachedPortfolioData) =>
    {
        try
        {
            sessionStorage.setItem(getStorageKey(portfolioModelId), JSON.stringify(data.tableData));
            sessionStorage.setItem(getChartStorageKey(portfolioModelId), JSON.stringify(data.chartData));
            console.log('Saved to sessionStorage for portfolio:', portfolioModelId);
        }
        catch(error)
        {
            console.error('Error saving to sessionStorage:', error);
        }
    }, []);

    const getFromSessionStorage = React.useCallback((portfolioModelId: string): CachedPortfolioData | null =>
    {
        try
        {
            const tableData = sessionStorage.getItem(getStorageKey(portfolioModelId));
            const chartData = sessionStorage.getItem(getChartStorageKey(portfolioModelId));
            
            if(!tableData || !chartData) return null;
            
            const parsedTableData = JSON.parse(tableData);
            const parsedChartData = JSON.parse(chartData);
            
            if(Array.isArray(parsedTableData) && parsedTableData.length > 0)
            {
                const expectedModelId = parseInt(portfolioModelId);
                const inconsistentData = parsedTableData.some((item: IPortfolioModelTable) => item.modelPortfolioId && item.modelPortfolioId !== expectedModelId);
                
                if(inconsistentData)
                {
                    sessionStorage.removeItem(getStorageKey(portfolioModelId));
                    sessionStorage.removeItem(getChartStorageKey(portfolioModelId));
                    return null;
                }
            }
            
            console.log('Loaded from sessionStorage for portfolio:', portfolioModelId);
            return{ 
                tableData: parsedTableData, 
                chartData: parsedChartData,
                isNoChartDataOnly: false
            };
        }
        catch(error)
        {
            console.error('Error reading from sessionStorage:', error);
            return null;
        }
    }, []);

    const formatDate = React.useCallback((isoDate: string): string =>
    {
        if(!isoDate) return '-';
        
        try
        {
            const date = new Date(isoDate);
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const year = date.getFullYear();
            return `${day}/${month}/${year}`;
        }
        catch(error)
        {
            console.error('Error formatting date:', error);
            return '-';
        }
    }, []);

    // Función para cargar datos de una cartera (tabla + gráfica)
    const loadPortfolioData = React.useCallback(async (portfolioId: string): Promise<CachedPortfolioData> =>
    {
        console.log('Loading portfolio data for:', portfolioId);
        
        // Llamada principal para obtener datos básicos de tabla y gráfica
        const response = await portfolioModelWallets.getFullPortfolioModelInformation(parseInt(portfolioId));
        console.log('API Response for portfolio', portfolioId, ':', response);

        // Verificar si la respuesta indica que no hay datos disponibles
        // Solo lanzar error si no hay datos de tabla tampoco
        if((response.data.message === "NO_DATA_AVAILABLE" || response.data.Message === "NO_DATA_AVAILABLE") && response.data?.data?.dataReportingPortfolio === null && (!response.data?.data?.dataPortfolio?.fundItems || response.data.data.dataPortfolio.fundItems.length === 0))
        {
            console.log('No data available for portfolio (no table data either):', portfolioId);
            throw new Error(`NO_DATA_AVAILABLE_${portfolioId}`);
        }

        console.log('Response structure:',
        {
            message: response.data.message || response.data.Message,
            dataKeys: response.data?.data ? Object.keys(response.data.data) : 'No data',
            series: response.data?.data?.dataReportingPortfolio?.series,
            dataPortfolio: response.data?.data?.dataPortfolio,
            fundItemsLength: response.data?.data?.dataPortfolio?.fundItems?.length || 0
        });

        const dataPortfolio = response.data?.data?.dataPortfolio;
        const basicTableData = (dataPortfolio?.fundItems || []).map((fundItem: any) =>
        {
            const isin = fundItem.Fund?.ISIN || '';
            return{
                nameFund: fundItem.Fund?.Name || '',
                isin: isin,
                portfolioPercentage: Number(fundItem.ModelPercentage) || 0,
                fundPrice: 0,
                liquidationDate: '-',
                currency: 'EUR',
                ironiaPoints: 0,
                portfolioName: selectedOptions.length > 1 ? options.find(opt => opt.key === portfolioId)?.text : undefined,
                modelPortfolioId: parseInt(portfolioId)
            };
        });

        // Procesar datos de gráfica - nueva estructura
        const series = response.data?.data?.dataReportingPortfolio?.series;
        const reportingData: SerieData[] = series || [];
        const chartData = reportingData.map((item: SerieData) =>
        {
            const timestamp = new Date(item.date).getTime();
            return [timestamp, item.cartera];
        });

        const hasTableData = basicTableData.length > 0;
        const hasChartData = chartData.length > 0;
        const isNoChartDataOnly = hasTableData && !hasChartData;

        // Obtener ISINs únicos para la llamada adicional
        const isins = basicTableData.map((item: IPortfolioModelTable) => item.isin).filter((isin: string) => isin.trim() !== '');
        
        let finalTableData = basicTableData;
        
        // Si hay ISINs, obtener información adicional
        if(isins.length > 0)
        {
            try
            {
                const additionalInfo: IPortfolioModelFullInformation[] = await FundService.getPortfolioModelFullInformation(isins);
                
                // Mapear información adicional
                const additionalInfoMap: {[isin: string]: IPortfolioModelFullInformation} = {};
                additionalInfo.forEach((fundInfo: IPortfolioModelFullInformation) =>
                {
                    additionalInfoMap[fundInfo.isin] = fundInfo;
                });

                // Combinar datos básicos con información adicional
                finalTableData = basicTableData.map((item: IPortfolioModelTable) =>
                {
                    const additionalData = additionalInfoMap[item.isin];
                    if(additionalData)
                    {
                        return{
                            ...item,
                            fundPrice: additionalData.vLiq,
                            liquidationDate: formatDate(additionalData.dateVLiq),
                            currency: additionalData.currency,
                            ironiaPoints: additionalData.ironiaPoints,
                            ytdLinearReturn: additionalData.ytdLinearReturn * 100,
                            oneYearVolatility: additionalData.oneYearVolatility * 100
                        };
                    }
                    return item;
                });
            }
            catch(error)
            {
                console.error('Error fetching additional fund info:', error);
            }
        }

        return{ 
            tableData: finalTableData, 
            chartData,
            isNoChartDataOnly 
        };
    }, [formatDate, options, selectedOptions.length]);

    // Función principal para procesar carteras seleccionadas
    const processSelectedPortfolios = React.useCallback(async (portfolioIds: string[]) =>
    {
        if(portfolioIds.length === 0)
        {
            setModelPortfolioComposition([]);
            setSeries([]);
            setNoDataAvailable(false);
            setNoChartDataOnly(false);
            return;
        }

        // Verificar si alguna cartera ya se está cargando
        const alreadyLoading = portfolioIds.some(id => loadingPortfolios.has(id));
        if(alreadyLoading)
        {
            console.log('Some portfolios are already loading, skipping request');
            return;
        }

        setIsLoading(true);
        setIsInterfaceDisabled(true);
        setNoDataAvailable(false);
        setNoChartDataOnly(false);
        console.log('Processing portfolios:', portfolioIds);

        try
        {
            const allTableData: IPortfolioModelTable[] = [];
            const allChartSeries: any[] = [];
            const colors = ['#00B050', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4'];
            let hasAnyNoChartDataOnly = false;

            for(let i = 0; i < portfolioIds.length; i++)
            {
                const portfolioId = portfolioIds[i];
                let portfolioData: CachedPortfolioData;

                // Verificar caché en sessionStorage
                const cachedData = getFromSessionStorage(portfolioId);
                if(cachedData)
                {
                    console.log('Loading from sessionStorage cache for portfolio:', portfolioId);
                    
                    // Verificar si los datos cacheados representan "sin datos"
                    const hasTableData = cachedData.tableData.length > 0;
                    const hasChartData = cachedData.chartData.length > 0;
                    
                    // Solo considera "sin datos" cuando no hay datos de tabla
                    if(!hasTableData)
                    {
                        console.log('Cached data has no table data for portfolio:', portfolioId);
                        
                        // Si solo hay una cartera seleccionada, mostrar el mensaje de error
                        if(portfolioIds.length === 1)
                        {
                            setNoDataAvailable(true);
                            setModelPortfolioComposition([]);
                            setSeries([]);
                            return;
                        }
                        else
                        {
                            // Si hay múltiples carteras, simplemente continuar con las otras
                            console.log('Skipping cached portfolio with no table data:', portfolioId);
                            continue;
                        }
                    }
                    
                    // Si hay datos de tabla pero no de gráfica, marcar como solo sin gráfica
                    if(hasTableData && !hasChartData)
                    {
                        hasAnyNoChartDataOnly = true;
                    }
                    
                    portfolioData = cachedData;
                }
                else
                {
                    // Marcar como en carga para evitar peticiones concurrentes
                    setLoadingPortfolios(prev => new Set([...Array.from(prev), portfolioId]));
                    
                    try
                    {
                        console.log('Loading from API for portfolio:', portfolioId);
                        portfolioData = await loadPortfolioData(portfolioId);
                        
                        // Guardar en caché
                        saveToSessionStorage(portfolioId, portfolioData);
                    }
                    catch(loadError: any)
                    {
                        // Verificar si es un error de "sin datos disponibles"
                        if(loadError.message && loadError.message.startsWith('NO_DATA_AVAILABLE'))
                        {
                            console.log('No data available for portfolio:', portfolioId);
                            
                            // Si solo hay una cartera seleccionada, mostrar el mensaje de error
                            if(portfolioIds.length === 1)
                            {
                                setNoDataAvailable(true);
                                setModelPortfolioComposition([]);
                                setSeries([]);
                                return;
                            }
                            else
                            {
                                // Si hay múltiples carteras, simplemente continuar con las otras
                                console.log('Skipping portfolio with no data:', portfolioId);
                                continue;
                            }
                        }
                        else
                        {
                            // Re-lanzar otros tipos de errores
                            throw loadError;
                        }
                    }
                    finally
                    {
                        setLoadingPortfolios(prev =>
                        {
                            const newSet = new Set(Array.from(prev));
                            newSet.delete(portfolioId);
                            return newSet;
                        });
                    }
                }

                // Verificar si esta cartera tiene datos de tabla pero no de gráfica
                if(portfolioData.isNoChartDataOnly)
                {
                    hasAnyNoChartDataOnly = true;
                }

                // Actualizar nombres de cartera si es necesario
                const updatedTableData = portfolioData.tableData.map((item: IPortfolioModelTable) =>
                ({
                    ...item,
                    portfolioName: portfolioIds.length > 1 ? options.find(opt => opt.key === portfolioId)?.text : undefined
                }));

                // Agregar datos de tabla
                allTableData.push(...updatedTableData);

                // Agregar datos de gráfica
                if(portfolioData.chartData.length > 0)
                {
                    const portfolioName = options.find(opt => opt.key === portfolioId)?.text || `Cartera ${portfolioId}`;
                    allChartSeries.push(
                    {
                        name: portfolioName,
                        data: portfolioData.chartData,
                        color: colors[i % colors.length]
                    });
                }
            }

            // Actualizar estados solo cuando todo esté listo
            console.log('Setting final table data:', allTableData);
            console.log('Setting final chart series:', allChartSeries);
            
            setModelPortfolioComposition(allTableData);
            setSeries(allChartSeries);
            
            // Si solo hay una cartera y no tiene datos de gráfica pero sí de tabla
            if(portfolioIds.length === 1 && hasAnyNoChartDataOnly)
            {
                setNoChartDataOnly(true);
            }
        }
        catch(error)
        {
            console.error('Error processing portfolios:', error);
        }
        finally
        {
            setIsLoading(false);
            setIsInterfaceDisabled(false);
        }
    }, [loadingPortfolios, getFromSessionStorage, loadPortfolioData, saveToSessionStorage, options]);

    // Manejar cambios en la selección
    const onSelectionChange = React.useCallback(async (event: React.FormEvent<HTMLDivElement>, item?: IDropdownOption): Promise<void> =>
    {
        if(item && !isInterfaceDisabled)
        {
            const newSelectedOptions = item.selected
                ? [...selectedOptions, item.key as string]
                : selectedOptions.filter(key => key !== item.key);

            console.log('Selection changed to:', newSelectedOptions);
            setSelectedOptions(newSelectedOptions);
            
            // Procesar inmediatamente las nuevas selecciones
            await processSelectedPortfolios(newSelectedOptions);
        }
    }, [selectedOptions, isInterfaceDisabled, processSelectedPortfolios]);

    // Funciones auxiliares
    const getSelectedPortfolioNames = () =>
    {
        return selectedOptions.map(key => options.find(opt => opt.key === key)?.text).filter(text => text !== undefined).join(', ');
    };

    const getHeaderTitle = () =>
    {
        if(selectedOptions.length === 0)
        {
            return 'Resumen de Cartera Modelo';
        }
        
        if(selectedOptions.length === 1)
        {
            const selectedModel = options.find(opt => opt.key === selectedOptions[0]);
            return `Resumen de Cartera Modelo - ${selectedModel?.text || ''}`;
        }
        
        return `Resumen de Carteras Modelo - ${selectedOptions.length} seleccionadas`;
    };

    // Función para manejar el click del botón Aportación Periódica
    const handlePeriodicContribution = React.useCallback(() =>
    {
        if(selectedOptions.length === 1 && location.state?.portfolioModelId && selectedOptions[0] === location.state.portfolioModelId.toString())
        {
            const portfolioModelId = location.state.portfolioModelId;
            const portfolioModelName = location.state.portfolioModelName;
            const savingPlanId = location.state.savingPlanId;
            const contract = location.state.contract;

            history.push(`/ModelPortfolio/PeriodicContributions/${portfolioModelId}`, 
            {
                portfolioModelId,
                portfolioModelName,
                savingPlanId,
                contract
            });
        }
    }, [selectedOptions, location.state, history]);

    // Funciones para manejar los nuevos botones
    const handleCounterClick = React.useCallback(() =>
    {
        if(counterValue > 1)
        {
            setIsModalOpen(true);
        }
    }, [counterValue]);

    const handleDateChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) =>
    {
        setSelectedDate(event.target.value);
        console.log('Date changed:', event.target.value);
        // TODO: Implementar lógica del cambio de fecha
    }, []);

    const handleEdit = React.useCallback(() =>
    {
        console.log('Edit clicked');
        // TODO: Implementar lógica de edición
    }, []);

    const handleDelete = React.useCallback(() =>
    {
        console.log('Delete clicked');
        // TODO: Implementar lógica de eliminación
    }, []);

    const handleAddComposition = React.useCallback(() =>
    {
        console.log('Add composition clicked');
        // TODO: Implementar lógica para añadir composición

        //Añadir composición -> Modal de Añadido

        //Pedimos fecha y buscador con los fondos con posibilidad de añadir

        //Preg1: - Todos los fondos incluidos no disponibles etc
        //Preg2: - Limitación de fondos en función de datos disponibles en BQ
        //Preg3: - ¿Si hay composiciones con fondos no disponibles actualmente que hacemos?
        //etc

        //Me importa nada como estuviera el fondo, solo importa si disponible, aqui y ahora para meter hoy.
        //En los fondos de hoy solo meto fondos disponibles hoy
        //Importaciones: Fondos disponibles hoy.
        //Enseñar la importacion
        //Comprobar que sumen 100, elegimos formato nosotros.
        
    }, []);

    const handleImportComposition = React.useCallback(() =>
    {
        console.log('Import composition clicked');
        setIsImportModalOpen(true);
    }, []);

    const handleExportCSV = React.useCallback(() =>
    {
        console.log('Export to CSV clicked');
        // TODO: Implementar exportación a CSV
    }, []);

    // Funciones para manejar el modal de registros
    const handleModalClose = React.useCallback(() =>
    {
        setIsModalOpen(false);
    }, []);

    const handleModalSelect = React.useCallback((selectedRecord: any) =>
    {
        console.log('Selected record:', selectedRecord);
        setIsModalOpen(false);
        
        // Buscar la composición correspondiente al registro seleccionado
        const selectedComposition = exampleCompositions.find(comp => comp.id === selectedRecord.id);
        
        if(selectedComposition)
        {
            // Actualizar la tabla con los datos de la composición seleccionada
            setModelPortfolioComposition(selectedComposition.tableData);
            setSelectedCompositionRecord(selectedRecord.id);
            console.log('Updated table with composition:', selectedComposition);
            console.log('Selected composition record ID:', selectedCompositionRecord);
        }
        else
        {
            console.warn('No composition found for selected record:', selectedRecord.id);
        }
    }, [exampleCompositions, selectedCompositionRecord]);

    // Funciones para manejar el modal de importación
    const handleImportModalClose = React.useCallback(() =>
    {
        setIsImportModalOpen(false);
    }, []);

    const handleImportData = React.useCallback((importData: any) =>
    {
        console.log('Import data:', importData);
        // TODO: Implementar la lógica de importación
        // Aquí procesaremos los datos importados y actualizaremos la tabla
        
        // Por ahora, solo mostramos los datos en consola
        console.log('File:', importData.file);
        console.log('Mode:', importData.mode);
        console.log('Date handling:', importData.dateHandling);
        console.log('Portfolio model name:', importData.portfolioModelName);
        
        setIsImportModalOpen(false);
    }, []);

    // Función para formatear el contador (máximo 99+)
    const formatCounter = React.useCallback((value: number): string =>
    {
        return value > 99 ? '99+' : value.toString();
    }, []);

    // Cargar datos cuando cambien las opciones seleccionadas
    React.useEffect(() =>
    {
        if(selectedOptions.length > 0)
        {
            setNoDataAvailable(false);
            setNoChartDataOnly(false);
            processSelectedPortfolios(selectedOptions);
        }
        else
        {
            setNoDataAvailable(false);
            setNoChartDataOnly(false);
        }
    }, [selectedOptions, processSelectedPortfolios]);

    return(
        <DocumentTitle title="IronIA - Resumen de Cartera Modelo">
            <React.Fragment>
                <HeaderContainer className="header">
                    <h2>
                        {getHeaderTitle()}
                    </h2>
                </HeaderContainer>
                <Container theme={theme}>
                    <Label theme={theme}>
                        {location.state?.savingPlanName ? `${location.state.savingPlanName}` : ''}
                    </Label>
                    <StyledDropdown multiSelect options={options} selectedKeys={selectedOptions} onChange={onSelectionChange} 
                        placeholder=
                        {
                            isInterfaceDisabled || isLoading 
                                ? "Procesando datos..." 
                                : "Selecciona una o más carteras Modelo"
                        }
                        disabled={isInterfaceDisabled || isLoading}
                        theme={theme}
                    />
                    {selectedOptions.length === 1 && location.state?.portfolioModelId && selectedOptions[0] === location.state.portfolioModelId.toString() &&
                    (
                        <ActionButton disabled={isInterfaceDisabled || isLoading} onClick={handlePeriodicContribution} theme={theme}>
                            {isInterfaceDisabled || isLoading ? "Procesando..." : "Aportación Periódica"}
                        </ActionButton>
                    )}
                    <CounterButton disabled={true} theme={theme}>
                        {formatCounter(counterValue)}
                    </CounterButton>
                    <DatePicker type="date" value={selectedDate} onChange={handleDateChange} disabled={isInterfaceDisabled || isLoading} theme={theme} />
                    <EditButton onClick={handleEdit} disabled={isInterfaceDisabled || isLoading} title="Editar" theme={theme}>
                        <Icon iconName="Edit" />
                    </EditButton>
                    <DeleteButton onClick={handleDelete} disabled={isInterfaceDisabled || isLoading} title="Eliminar" theme={theme}>
                        <Icon iconName="Delete" />
                    </DeleteButton>
                    <MainButton onClick={handleAddComposition} disabled={isInterfaceDisabled || isLoading} theme={theme}>
                        Añadir Composición
                    </MainButton>
                    <ExportIcon onClick={isInterfaceDisabled || isLoading ? undefined : handleImportComposition} className={isInterfaceDisabled || isLoading ? 'disabled' : ''} title="Importar Composición" theme={theme}>
                        <ExcelLogoIcon />
                    </ExportIcon>
                    <ExportIcon onClick={true ? undefined : handleExportCSV} className={true ? 'disabled' : ''} title="Exportar a CSV" theme={theme}>
                        <ExcelDocumentIcon />
                    </ExportIcon>
                </Container>
                <CardsContainer>
                    <Card className="portfolio-card">
                        <CardTitle>
                            {selectedOptions.length > 1 ? 'Carteras Modelo Seleccionadas' : 'Cartera Modelo Seleccionada'}
                        </CardTitle>
                        <CardData>
                            {selectedOptions.length > 0 ? getSelectedPortfolioNames() : 'No seleccionada'}
                        </CardData>
                    </Card>
                    <MetricsContainer>
                        <Card className="metric-card">
                            <CardTitle>Desde inicio</CardTitle>
                            <MetricCardData>
                                {metrics.fromStart < "0,00" && <DecrementIcon />}
                                {metrics.fromStart > "0,00" && <IncrementIcon />}
                                <span>{metrics.fromStart}%</span>
                            </MetricCardData>
                        </Card>
                        <Card className="metric-card">
                            <CardTitle>YTD</CardTitle>
                            <MetricCardData>
                                {metrics.ytd < "0,00" && <DecrementIcon />}
                                {metrics.ytd > "0,00" && <IncrementIcon />}
                                <span>{metrics.ytd}%</span>
                            </MetricCardData>
                        </Card>
                        <Card className="metric-card">
                            <CardTitle>6 meses</CardTitle>
                            <MetricCardData>
                                {metrics.lastSixMonths < "0,00" && <DecrementIcon />}
                                {metrics.lastSixMonths > "0,00" && <IncrementIcon />}
                                <span>{metrics.lastSixMonths}%</span>
                            </MetricCardData>
                        </Card>
                        <Card className="metric-card">
                            <CardTitle>Año</CardTitle>
                            <MetricCardData>
                                {metrics.year < "0,00" && <DecrementIcon />}
                                {metrics.year > "0,00" && <IncrementIcon />}
                                <span>{metrics.year}%</span>
                            </MetricCardData>
                        </Card>
                    </MetricsContainer>
                </CardsContainer>
                <GraphicsContainer>
                    <GraphicTitle>
                        Rentabilidad de tu cartera (TWR)
                    </GraphicTitle>
                    {isLoading ?
                    (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '1rem' }}>
                            <Spinner size={SpinnerSize.large} />
                            <span>Cargando datos de la cartera...</span>
                        </div>
                    )
                    : noDataAvailable && selectedOptions.length === 1 ?
                    (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '1rem', color: '#666' }}>
                            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>No hay datos asociados a esa Cartera</span>
                            <span style={{ fontSize: '14px' }}>Selecciona otra cartera modelo para ver su información.</span>
                        </div>
                    )
                    : noChartDataOnly && selectedOptions.length === 1 ?
                    (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', gap: '1rem', color: '#666' }}>
                            <span style={{ fontSize: '18px', fontWeight: 'bold' }}>No hay datos de gráfica para esta Cartera</span>
                            <span style={{ fontSize: '14px' }}>La información de la tabla está disponible más abajo.</span>
                        </div>
                    ) :
                    (
                        <Highstock series={series} customOptions={{}} graphicType={GraphicType.linearGraphic} />
                    )}
                </GraphicsContainer>
                <PortfolioModelTable data={modelPortfolioComposition} isLoading={isLoading} showPortfolioColumn={selectedOptions.length > 1} />
                <RecordsModal isOpen={isModalOpen} onClose={handleModalClose} onSelect={handleModalSelect} records={modalRecords} 
                    portfolioModelName={selectedOptions.length === 1 ? options.find(opt => opt.key === selectedOptions[0])?.text : undefined}
                    showPortfolioColumn={selectedOptions.length > 1}
                />
                <ImportComposition isOpen={isImportModalOpen} onClose={handleImportModalClose} 
                    onImport={handleImportData}
                    portfolioModelName={selectedOptions.length === 1 ? options.find(opt => opt.key === selectedOptions[0])?.text : undefined}
                />
            </React.Fragment>
        </DocumentTitle>
    );
}

export default SummaryPortfolioModel;